export type SessionEventType =
  | "session:authenticated"
  | "session:logged-out"
  | "session:refresh-started"
  | "session:refresh-finished"
  | "session:refresh-failed";

export interface BroadcastChannelPort {
  postMessage(message: unknown): void;
  addEventListener(
    type: "message",
    listener: (event: { data: unknown }) => void,
  ): void;
  removeEventListener(
    type: "message",
    listener: (event: { data: unknown }) => void,
  ): void;
  close(): void;
}

export interface StoragePort {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface StorageEventPort {
  addEventListener(
    type: "storage",
    listener: (event: { key: string | null; newValue: string | null }) => void,
  ): void;
  removeEventListener(
    type: "storage",
    listener: (event: { key: string | null; newValue: string | null }) => void,
  ): void;
}

export interface LockManagerPort {
  request(
    name: string,
    options: { readonly mode: "exclusive"; readonly ifAvailable: true },
    callback: (lock: { readonly name: string } | null) => Promise<void>,
  ): Promise<void>;
}

export interface RefreshLease {
  release(): Promise<void>;
}

export interface SessionEvents {
  publish(type: SessionEventType): void;
  subscribe(listener: (type: SessionEventType) => void): () => void;
  tryAcquireRefreshLease(): Promise<RefreshLease | null>;
  refreshLeaseRemainingMs(): number;
  dispose(): void;
}

export interface SessionEventsOptions {
  readonly leaseMs: number;
  readonly channelName?: string;
  readonly eventStorageKey?: string;
  readonly leaseStorageKey?: string;
  readonly channelFactory?: (name: string) => BroadcastChannelPort | null;
  readonly storage?: StoragePort | null;
  readonly storageEvents?: StorageEventPort | null;
  readonly lockManager?: LockManagerPort | null;
  readonly now?: () => number;
  readonly instanceId?: string;
}

interface EventWire {
  readonly v: 1;
  readonly type: SessionEventType;
  readonly nonce: string;
}

interface LeaseWire {
  readonly v: 1;
  readonly owner: string;
  readonly expiresAt: number;
}

const EVENT_TYPES = new Set<SessionEventType>([
  "session:authenticated",
  "session:logged-out",
  "session:refresh-started",
  "session:refresh-finished",
  "session:refresh-failed",
]);

const DEFAULT_CHANNEL_NAME = "gocanvas:session";
const DEFAULT_EVENT_KEY = "gocanvas:session:event";
const DEFAULT_LEASE_KEY = "gocanvas:session:refresh-lease";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseEvent(value: unknown): EventWire | null {
  if (!isRecord(value) || Object.keys(value).length !== 3) return null;
  if (
    value.v !== 1 ||
    typeof value.type !== "string" ||
    !EVENT_TYPES.has(value.type as SessionEventType) ||
    typeof value.nonce !== "string" ||
    value.nonce.length === 0 ||
    value.nonce.length > 128
  ) {
    return null;
  }
  return { v: 1, type: value.type as SessionEventType, nonce: value.nonce };
}

function parseEventJSON(raw: string | null): EventWire | null {
  if (!raw) return null;
  try {
    return parseEvent(JSON.parse(raw));
  } catch {
    return null;
  }
}

function parseLease(raw: string | null): LeaseWire | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || Object.keys(value).length !== 3) return null;
    if (
      value.v !== 1 ||
      typeof value.owner !== "string" ||
      value.owner.length === 0 ||
      value.owner.length > 128 ||
      typeof value.expiresAt !== "number" ||
      !Number.isSafeInteger(value.expiresAt)
    ) {
      return null;
    }
    return { v: 1, owner: value.owner, expiresAt: value.expiresAt };
  } catch {
    return null;
  }
}

function defaultInstanceId(): string {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to a document-local non-secret identifier.
  }
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function defaultStorage(): StoragePort | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function defaultStorageEvents(): StorageEventPort | null {
  return typeof window === "undefined" ? null : window;
}

function defaultChannelFactory(name: string): BroadcastChannelPort | null {
  try {
    return typeof BroadcastChannel === "undefined"
      ? null
      : new BroadcastChannel(name);
  } catch {
    return null;
  }
}

function defaultLockManager(): LockManagerPort | null {
  try {
    return typeof navigator === "undefined" || !("locks" in navigator)
      ? null
      : (navigator.locks as unknown as LockManagerPort);
  } catch {
    return null;
  }
}

class SessionEventsController implements SessionEvents {
  readonly #listeners = new Set<(type: SessionEventType) => void>();
  readonly #channel: BroadcastChannelPort | null;
  readonly #storage: StoragePort | null;
  readonly #storageEvents: StorageEventPort | null;
  readonly #lockManager: LockManagerPort | null;
  readonly #eventStorageKey: string;
  readonly #leaseStorageKey: string;
  readonly #now: () => number;
  readonly #instanceId: string;
  #counter = 0;
  #leaseCounter = 0;
  #disposed = false;
  #activeLease: RefreshLease | null = null;
  #activeLockRequest: Promise<void> | null = null;

  readonly #onMessage = (event: { data: unknown }): void => {
    this.#receive(parseEvent(event.data));
  };

  readonly #onStorage = (event: {
    key: string | null;
    newValue: string | null;
  }): void => {
    if (event.key !== this.#eventStorageKey) return;
    this.#receive(parseEventJSON(event.newValue));
  };

  constructor(private readonly options: SessionEventsOptions) {
    if (!Number.isSafeInteger(options.leaseMs) || options.leaseMs <= 0) {
      throw new Error("session event leaseMs must be a positive integer");
    }
    this.#now = options.now ?? Date.now;
    this.#instanceId = options.instanceId ?? defaultInstanceId();
    if (this.#instanceId === "" || this.#instanceId.length > 128) {
      throw new Error("session event instanceId must be non-empty and bounded");
    }
    this.#eventStorageKey = options.eventStorageKey ?? DEFAULT_EVENT_KEY;
    this.#leaseStorageKey = options.leaseStorageKey ?? DEFAULT_LEASE_KEY;
    this.#storage =
      options.storage === undefined ? defaultStorage() : options.storage;
    this.#storageEvents =
      options.storageEvents === undefined
        ? defaultStorageEvents()
        : options.storageEvents;
    this.#lockManager =
      options.lockManager === undefined
        ? defaultLockManager()
        : options.lockManager;
    const channelFactory = options.channelFactory ?? defaultChannelFactory;
    this.#channel = channelFactory(options.channelName ?? DEFAULT_CHANNEL_NAME);

    if (this.#channel) this.#channel.addEventListener("message", this.#onMessage);
    else this.#storageEvents?.addEventListener("storage", this.#onStorage);
  }

  publish(type: SessionEventType): void {
    if (this.#disposed || !EVENT_TYPES.has(type)) return;
    this.#counter += 1;
    const wire: EventWire = {
      v: 1,
      type,
      nonce: `${this.#instanceId}:${this.#counter}`,
    };
    if (this.#channel) {
      try {
        this.#channel.postMessage(wire);
      } catch {
        // A dead notification channel cannot change local session truth.
      }
      return;
    }

    if (!this.#storage) return;
    try {
      this.#storage.setItem(this.#eventStorageKey, JSON.stringify(wire));
      this.#storage.removeItem(this.#eventStorageKey);
    } catch {
      // Disabled/quota-limited storage degrades to per-tab verification.
    }
  }

  subscribe(listener: (type: SessionEventType) => void): () => void {
    if (this.#disposed) return () => undefined;
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  async tryAcquireRefreshLease(): Promise<RefreshLease | null> {
    if (this.#disposed) return null;
    const activeRequest = this.#activeLockRequest;
    if (activeRequest) {
      try {
        await activeRequest;
      } catch {
        // The next acquisition still gets one fresh fail-closed attempt.
      }
      if (this.#disposed) return null;
    }
    if (!this.#lockManager) return null;
    return this.#tryAcquireBrowserLock();
  }

  refreshLeaseRemainingMs(): number {
    if (!this.#storage) return 0;
    try {
      const lease = parseLease(this.#storage.getItem(this.#leaseStorageKey));
      return lease ? Math.max(0, lease.expiresAt - this.#now()) : 0;
    } catch {
      return 0;
    }
  }

  #removeOwnLeaseMetadata(owner: string): void {
    if (!this.#storage) return;
    try {
      const lease = parseLease(this.#storage.getItem(this.#leaseStorageKey));
      if (lease?.owner === owner)
        this.#storage.removeItem(this.#leaseStorageKey);
    } catch {
      // Best-effort; every lease is bounded and can expire.
    }
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    if (this.#channel) {
      this.#channel.removeEventListener("message", this.#onMessage);
      this.#channel.close();
    } else {
      this.#storageEvents?.removeEventListener("storage", this.#onStorage);
    }
    void this.#activeLease?.release();
    this.#listeners.clear();
  }

  async #tryAcquireBrowserLock(): Promise<RefreshLease | null> {
    const lockManager = this.#lockManager;
    if (!lockManager) return null;

    let settle!: (lease: RefreshLease | null) => void;
    let settled = false;
    const acquired = new Promise<RefreshLease | null>((resolve) => {
      settle = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
    });

    try {
      const request = lockManager.request(
        this.#leaseStorageKey,
        { mode: "exclusive", ifAvailable: true },
        async (lock) => {
          if (!lock || this.#disposed) {
            settle(null);
            return;
          }

          let unlock!: () => void;
          const held = new Promise<void>((resolve) => {
            unlock = resolve;
          });
          const owner = this.#nextLeaseOwner();
          let released = false;
          const lease: RefreshLease = {
            release: async () => {
              if (released) return;
              released = true;
              this.#removeOwnLeaseMetadata(owner);
              unlock();
              try {
                await request;
              } catch {
                // The capability is released even if the browser request failed.
              }
            },
          };
          this.#activeLease = lease;
          this.#writeLeaseMetadata(owner);
          settle(lease);
          try {
            await held;
          } finally {
            if (this.#activeLease === lease) this.#activeLease = null;
          }
        },
      );
      this.#activeLockRequest = request;
      void request
        .catch(() => settle(null))
        .finally(() => {
          if (this.#activeLockRequest === request) this.#activeLockRequest = null;
        });
    } catch {
      settle(null);
    }
    return acquired;
  }

  #nextLeaseOwner(): string {
    this.#leaseCounter += 1;
    const suffix = `:${this.#leaseCounter.toString(36)}`;
    return `${this.#instanceId.slice(0, 128 - suffix.length)}${suffix}`;
  }

  #writeLeaseMetadata(owner: string): void {
    if (!this.#storage) return;
    try {
      const candidate: LeaseWire = {
        v: 1,
        owner,
        expiresAt: this.#now() + this.options.leaseMs,
      };
      this.#storage.setItem(this.#leaseStorageKey, JSON.stringify(candidate));
    } catch {
      // Web Locks remains authoritative when storage is unavailable.
    }
  }

  #receive(wire: EventWire | null): void {
    if (this.#disposed || !wire || wire.nonce.startsWith(`${this.#instanceId}:`))
      return;
    for (const listener of [...this.#listeners]) {
      try {
        listener(wire.type);
      } catch {
        // One tab-local observer cannot block other listeners.
      }
    }
  }
}

export function createSessionEvents(
  options: SessionEventsOptions,
): SessionEvents {
  return new SessionEventsController(options);
}
